import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@nrwl/environment';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Roles } from '@nrwl/rx-models';
import { UserStoreSelector } from '@nrwl/rx-state-user';
import { withFormType } from '../form-query/form-query-params.utils';

@Injectable({
  providedIn: 'root',
})
export class DiagnosisTreatmentsService {
  constructor(
    private http: HttpClient,
    private store: Store,
  ) {}

  getUsedDiagnosis() {
    return this.http.get<any>(environment.baseAPIUrlv3 + `diagnose/?is_used=1`);
  }

  getUsedTreatments() {
    return this.http.get<any>(environment.baseAPIUrlv3 + `treatment/?is_used=1`);
  }

  getUsedSurgeons() {
    return this.http.get<any>(environment.baseAPIUrlv3 + `surgeon/?is_used=true&limit=1000`).pipe(
      map((data) => {
        const sortedData = data.results.sort((prev, next) =>
          prev.last_name.localeCompare(next.last_name),
        );
        const namedData = sortedData.map((item) => {
          if (!item.name) {
            item.name = item.last_name + ', ' + item.first_name;
          }
          return item;
        });
        return {
          ...data,
          results: namedData,
        };
      }),
    );
  }

  getUsedVisitTypes() {
    return this.http
      .get<any>(environment.baseAPIUrlv3 + `visit-type/?is_used=true&limit=1000`)
      .pipe(
        map((data) => {
          const sortedData = data.results.sort((prev, next) => prev.name.localeCompare(next.name));
          return {
            ...data,
            results: sortedData,
          };
        }),
      );
  }

  getUsedProcedureTypes() {
    return this.http
      .get<any>(environment.baseAPIUrlv3 + `procedure-type/?is_used=true&limit=1000`)
      .pipe(
        map((data) => {
          const sortedData = data.results.sort((prev, next) => prev.name.localeCompare(next.name));
          return {
            ...data,
            results: sortedData,
          };
        }),
      );
  }

  getUsedBodyParts() {
    return this.http
      .get<any>(environment.baseAPIUrlv3 + `body-part/?is_used=true&fields=id,name`)
      .pipe(
        map((data) => {
          return {
            ...data,
            results: data.results,
          };
        }),
      );
  }

  getFormTitlesForFilter() {
    // The clients and Forms inbox filters list form titles only; plans have their own filter.
    // The forms route returns every type unless asked (backend RXP-1277).
    const params = withFormType(
      new HttpParams()
        .set('unique', 'true')
        .set('fields', 'title')
        .set('limit', '100000')
        .set('is_deleted', 'false'),
      { formType: 'form' },
    );

    return this.http.get<any>(environment.baseAPIUrlv3 + 'form/', { params }).pipe(
      map((data) => {
        return {
          ...data,
          results: data.results,
        };
      }),
    );
  }

  getFormProvidersForFilter() {
    return this.store.select(UserStoreSelector.getUser).pipe(
      filter((user) => !!user),
      take(1),
      switchMap((user) => {
        const endpoint = Number(user?.role) === Roles.Employee ? 'surgeon' : 'doctor';
        return this.http.get<any>(
          environment.baseAPIUrlv3 +
            `${endpoint}/?has_forms_assigned=true&limit=1000&ordering=last_name,first_name`,
        );
      }),
    );
  }

  getFormTemplate(params = {}) {
    return this.http.get<Observable<any>>(environment.baseAPIUrlv3 + `form-template/`, { params });
  }

  getFormCategory() {
    return this.http.get<Observable<any>>(environment.baseAPIUrlv3 + `form-category/`);
  }

  getAreaImplants() {
    return this.http.get<any>(environment.baseAPIUrlv3 + `area/?limit=1000`);
  }

  getImplantVolumes() {
    return this.http.get<any>(environment.baseAPIUrlv3 + `implant-volume?limit=1000`);
  }

  getImplantPlanes() {
    return this.http.get<any>(environment.baseAPIUrlv3 + `implant-plane?limit=1000`);
  }

  getBMIData() {
    return this.http.get<any>(environment.baseAPIUrlv3 + `bmi/`);
  }

  getSkinToneData() {
    return this.http.get<any>(environment.baseAPIUrlv3 + `skin-tone/?limit=1000`);
  }
}
